import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCandidateById, updateCandidate } from '../../store/slices/staffingSlice';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import StatusBadge from '../../components/StatusBadge';
import CommentSection from '../../components/CommentSection';
import StatusHistory from '../../components/StatusHistory';
import { STAFFING_CANDIDATE_STATUS, COMMENT_ENTITY_TYPES } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';

const StaffingCandidateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentCandidate, loading } = useSelector((state) => state.staffing);
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (id && id !== 'new') {
      dispatch(fetchCandidateById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentCandidate) {
      setFormData({
        name: currentCandidate.name,
        skills: Array.isArray(currentCandidate.skills)
          ? currentCandidate.skills.join(', ')
          : currentCandidate.skills,
        experience: currentCandidate.experience,
        salary: currentCandidate.salary || '',
        status: currentCandidate.status
      });
    }
  }, [currentCandidate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const submitData = {
        ...formData,
        skills: typeof formData.skills === 'string'
          ? formData.skills.split(',').map(s => s.trim()).filter(s => s)
          : formData.skills
      };

      await dispatch(updateCandidate({ id, data: submitData })).unwrap();
      dispatch(fetchCandidateById(id));
      setEditing(false);
    } catch (error) {
      alert('Failed to update candidate: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading || !currentCandidate) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid">
        <button
          className="btn btn-outline-secondary mb-3"
          onClick={() => navigate(`/staffing/requirements/${currentCandidate.requirement_id?._id || currentCandidate.requirement_id}`)}
        >
          <i className="ti ti-arrow-left me-1" />
          Back to Requirement
        </button>

        <div className="card mb-3">
          <div className="card-body">
            {editing ? (
              <form onSubmit={handleUpdate}>
                <h2 className="fs-5 mb-3">Edit Candidate</h2>

                <div className="mb-3">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Skills *</label>
                  <input
                    type="text"
                    name="skills"
                    className="form-control"
                    value={formData.skills}
                    onChange={handleChange}
                    required
                  />
                  <small className="text-muted d-block mt-1">
                    Enter skills separated by commas
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Experience *</label>
                  <input
                    type="text"
                    name="experience"
                    className="form-control"
                    value={formData.experience}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Salary</label>
                  <input
                    type="text"
                    name="salary"
                    className="form-control"
                    value={formData.salary}
                    onChange={handleChange}
                    placeholder="e.g., $60,000 per annum"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    {Object.values(STAFFING_CANDIDATE_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setEditing(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h2 className="fs-4 mb-2">{currentCandidate.name}</h2>
                    <StatusBadge status={currentCandidate.status} />
                  </div>
                  <button className="btn btn-outline-primary" onClick={() => setEditing(true)}>
                    Edit
                  </button>
                </div>

                <div className="row g-3 mt-3">
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">SKILLS</label>
                    <p className="mb-0">{Array.isArray(currentCandidate.skills)
                      ? currentCandidate.skills.join(', ')
                      : currentCandidate.skills}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">EXPERIENCE</label>
                    <p className="mb-0">{currentCandidate.experience}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">SALARY</label>
                    <p className="mb-0">{currentCandidate.salary || 'Not specified'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">REQUIREMENT</label>
                    <p className="mb-0">{currentCandidate.requirement_id?.title || 'N/A'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CLIENT</label>
                    <p className="mb-0">{currentCandidate.requirement_id?.client_id?.name || 'N/A'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">SUBMITTED ON</label>
                    <p className="mb-0">{formatDateTime(currentCandidate.created_at)}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <StatusHistory history={currentCandidate.status_history} />

        <CommentSection
          entityType={COMMENT_ENTITY_TYPES.STAFFING_CANDIDATE}
          entityId={id}
        />
      </div>
    </Layout>
  );
};

export default StaffingCandidateDetail;
